import { useState, useEffect, useCallback } from "react"
import { useSelector } from "react-redux"
import { load, Cookie } from "@kiwicom/cookies"
import JsCookie from "js-cookie"
import { bidSelector } from "../../../services/booking/booking"
import { useGetIsLoggedIn } from "@kiwicom/nitro/lib/services/auth/context"

export const EMAIL_TEMPLATES_FOR_AB_TEST = [
  "t-email_payment_accepted",
  "t-email_confirmed",
  "t-email_additional_passenger_information_required",
] as const

const REQUIRED_LOGIN_CLOSED_COOKIE = "requiredLoginClosed"

type UseRequiredLoginModal = {
  isRequiredModalVisible: boolean
  onCloseRequiredModal: () => void
}

/**
 * Required login modal is displayed for not logged users coming to MMB from three
 * specific email templates on 50% traffic of the AB test.
 *
 * Tracking events of "modal shown", "sign in clicked" and "continue as guest clicked" are
 * already implemented in Nitro's MagicLogin.
 *
 * Once modal is closed, we set a cookie "requiredLoginClosed", so that required modal
 * doesn't appear again after page is refreshed if user clicked "Continue as guest" before.
 *
 * @returns
 * - isRequiredModalVisible (value based on user being logged out, email template and AB test value)
 * - onCloseRequiredModal (required because Nitro doesn't close the modal automatically).
 */
const useRequiredLoginModal = (): UseRequiredLoginModal => {
  const [isRequiredModalVisible, setIsRequiredModalVisible] = useState<boolean>(false)
  // @ts-expect-error: Flow to TS migration.
  const query = useSelector((state) => state.query)
  const isLoggedIn = useGetIsLoggedIn()
  const wasRequiredLoginClosed = JsCookie.get(REQUIRED_LOGIN_CLOSED_COOKIE) === "true"
  const bid = useSelector(bidSelector)
  const simpleTokenCookie = JSON.parse(load(Cookie.SIMPLE_TOKEN) ?? "{}")
  const simpleToken = simpleTokenCookie.bid === bid ? simpleTokenCookie.token : null

  const { utm_campaign: utmCampaign } = query

  useEffect(() => {
    void isLoggedIn().then((loggedIn) => {
      const shouldDisplayRequiredLogin =
        simpleToken &&
        !loggedIn &&
        !wasRequiredLoginClosed &&
        EMAIL_TEMPLATES_FOR_AB_TEST.includes(utmCampaign)
      if (shouldDisplayRequiredLogin) {
        setIsRequiredModalVisible(true)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utmCampaign, wasRequiredLoginClosed, simpleToken])

  const onCloseRequiredModal = useCallback(() => {
    setIsRequiredModalVisible(false)
    JsCookie.set(REQUIRED_LOGIN_CLOSED_COOKIE, "true", { expires: 1 / 24 }) // expires in 1h
  }, [])

  return { isRequiredModalVisible, onCloseRequiredModal }
}

export default useRequiredLoginModal
